import React from 'react'
import { Alert, Button, Col, Form, message, Popconfirm, Progress,  Row, Select, Skeleton, Switch, Tag, Typography } from 'antd'
import * as deepObject from 'deep-object-diff'
import { FormComponent } from '../../../BaseComponent'
import { IntegrationFormProps } from '../'
import IIntegrationSync, { SyncSectionNameArray } from '../../../../interfaces/IIntegrationSync'
import { SyncOneRequest, SyncAbortRequest, VerifySyncRequest } from '../../../../services/integration/interfaces/ApiRequest'
import { IntegrationConstants } from '../../../../helpers/enums/Integration'
import DynamicTimeline from '../../../DynamicTimeline'

type IntegrationSyncProps = IntegrationFormProps & {
  sync_uuid?: string,
  sync: IIntegrationSync | null,

  loading: boolean,

  verifySync: (args: VerifySyncRequest) => Promise<any>,
  syncOne: (args: SyncOneRequest) => Promise<any>,
  syncAbort: (args: SyncAbortRequest) => Promise<any>
}

export default class IntegrationSync extends FormComponent<IntegrationSyncProps> {

  state = {
    loaded: false,
    exists: false,
    running: false,
    aborting: false,
    progress: 0
  }

  private abortUpdate = false

  async componentDidMount() {
    const { loading, sync_uuid, verifySync } = this.props

    if (!loading && sync_uuid) {
      const sync = await verifySync({
        sync_uuid
      })

      this.updateSync(sync)
    }
  }

  componentWillUnmount() {
    this.abortUpdate = true
  }

  componentDidUpdate(prevProps: IntegrationSyncProps, prevState) {
    const { sync } = this.props
    const wasLoading = prevProps.loading && !this.props.loading

    const newState = Object.assign({}, this.state)

    if (!this.state.loaded && wasLoading) {
      newState.loaded = true

      if (sync) {
        newState.exists = true
      }
    }

    if (this.state.loaded) {
      if (wasLoading && this.state.aborting) {
        newState.aborting = false
      }
    }

    const hasChanged = Object.keys(deepObject.diff(this.state, newState)).length > 0
    if (hasChanged) {
      this.setState(newState)
    }
  }

  private updateSync = (sync: IIntegrationSync | null) => {
    if (sync) {
      const newState = Object.assign({}, this.state)

      // Is running
      newState.running = !sync.finish && !sync.canceled

      if (newState.running) {
        if (sync.total > 0) {
          // Grab progress
          newState.progress = Math.round((sync.current / sync.total) * 100)
        }
      }

      const hasChanged = Object.keys(deepObject.diff(this.state, newState)).length > 0

      if (hasChanged) {
        this.setState(newState)
      }
    }

    if (this.state.running) {
      setTimeout(async () => {
        if (this.abortUpdate) return

        if (this.props.sync) {
          await this.props.verifySync({
            sync_uuid: this.props.sync?.uuid
          })
        }

        this.updateSync(this.props.sync)
      }, 1000)
    }
  }

  private handleMakeSync = async (values: any) => {
    const { integrationUuid, syncOne } = this.props
    const { running } = this.state

    if (!running && integrationUuid) {
      try {
        const sync = await syncOne({
          integration_uuid: integrationUuid,
          forceSections: values.force || null,
          forceUpdates: values.forceUpdates || false
        })

        this.updateSync(sync)
      } catch (err) {
        message.error(err, 5)
      }
    }
  }

  private handleCancelSync = async () => {
    const { sync, syncAbort } = this.props
    const { aborting } = this.state
    if (!sync) {
      return message.error('Sync does not exists anymore!')
    }
    if (aborting) return

    this.setState({
      aborting: true,

    })
    syncAbort({
      sync_uuid: sync.uuid
    })
  }

  private renderForceSections = () => {
    const { sync } = this.props
    if (!sync?.force_sections) return null

    return sync.force_sections.split(',').map((section, index) => {
      const tag = IntegrationConstants.SYNC_SECTIONS_TAGS[section.toLowerCase()] || {
        label: section,
        color: 'blue'
      }

      return <Tag key={`sync-section-${index}`} color={tag.color} style={{ marginBottom: 5 }}>{tag.label}</Tag>
    })
  }

  private renderProgress = () => {
    const { sync } = this.props
    const { running, progress } = this.state

    if (!running && sync?.canceled) {
      return <Progress type="circle" percent={progress} status="exception" />
    }

    return <Progress type="circle" percent={progress} />
  }

  private renderStages = () => {
    const { sync } = this.props
    if (!sync) return null

    const timeline = { ...IntegrationConstants.SYNC_TIMELINE }

    Object.keys(timeline).forEach(key => {
      const item = sync[key]
      if (item !== undefined) {
        timeline[key] = {
          ...timeline[key],
          color: 'blue',
          value: item
        }
      }

      if (item === undefined && !timeline[key].custom) {
        delete timeline[key]
      }
    })

    timeline['created_date'] = {
      ...timeline['created_date'],
      order: 1,
      color: 'blue',
      label: sync.created_date ? 'Started at' : 'Started',
      value: sync.created_date
    }

    // Has finished?
    if (sync.finish) {
      timeline['status'] = {
        ...timeline['status'],
        color: 'green',
        label: 'Finished',
        value: sync.process_time
      }
    }

    // Has been canceled
    if (sync.canceled) {
      timeline['status'] = {
        ...timeline['status'],
        color: 'red',
        label: 'Canceled',
        value: sync.process_time
      }
    }

    return <DynamicTimeline items={timeline} />
  }

  private renderMakeSyncForm = () => {
    const { loading } = this.props
    const { running, aborting } = this.state

    if (running || aborting) {
      return (
        <>
          <Alert
            message="Sync"
            description="Sync is running!"
            type="success"
            showIcon
          />

          <Popconfirm
            title="Are you sure about aborting the current sync?"
            placement="topRight"
            okText="Yes"
            cancelText="No"
            onConfirm={this.handleCancelSync}
            style={{
              marginTop: 15
            }}
          >
            <Button
              type="dashed"
              size="middle"
              loading={aborting}
              style={{
                marginTop: 15
              }}
              block
            >
              Abort sync
            </Button>
          </Popconfirm>
        </>
      )
    }

    const options = SyncSectionNameArray.map(type => {
      return {
        label: type,
        value: type
      }
    })

    return (
      <Form
        name="integration_sync"
        layout="vertical"
        onFinish={this.handleMakeSync}
      >
        <Form.Item
          name="force"
          label="Force Sections"
        >
          <Select mode="multiple" size="middle" options={options} allowClear={true} />
        </Form.Item>

        <Form.Item
          name="forceUpdates"
          label="Force Updates"
          valuePropName="checked"
        >
          <Switch/>
        </Form.Item>

        <Form.Item style={{ marginTop: 32 }}>
          <Button type="primary" size="middle" htmlType="submit" loading={loading} block>
            Sync
          </Button>
        </Form.Item>
      </Form>
    )
  }

  render() {
    const { loaded } = this.state

    if (!loaded) {
      return <Skeleton />
    }
    // if (!exists) {
    //   return (
    //     <Result
    //       status="404"
    //       title="Sync not found"
    //       subTitle="This integration has not made any sync until now."
    //       extra={<Button type="primary">Make the frist sync</Button>}
    //     />
    //   )
    // }

    return (
      <Row gutter={24}>
        <Col xs={24} md={4}>
          <Typography.Title level={5}>Force Sections</Typography.Title>
          {this.renderForceSections()}
        </Col>

        <Col xs={24} md={8}>
          <Typography.Title level={5}>Progress</Typography.Title>
          {this.renderProgress()}
        </Col>

        <Col xs={24} md={6}>
          <Typography.Title level={5}>Stages</Typography.Title>
          {this.renderStages()}
        </Col>

        <Col xs={24} md={6}>
          <Typography.Title level={5}>Make new sync</Typography.Title>
          {this.renderMakeSyncForm()}
        </Col>
      </Row>
    )
  }
}
